/**
 * Smart Features Bar
 * Barre de fonctionnalités intelligentes pour l'UI principale
 */

import { useState, useEffect, useRef } from 'react';
import { 
  aiContextService, 
  recipesService, 
  alertsService,
  collaborativeService 
} from '../services/features.service';
import { PromoAlert, SmartSuggestions, Recipe } from '../types/features';

interface SmartFeaturesBarProps {
  userId: string;
  currentListId?: number;
  onAddItems?: (items: any[]) => void;
}

// Suggestions par défaut pour nouveaux utilisateurs
const DEFAULT_SUGGESTIONS = {
  frequent: [
    { name: 'Lait', quantity: 1, reason: 'Produit de base essentiel' },
    { name: 'Pain', quantity: 1, reason: 'Aliment quotidien' },
    { name: 'Œufs', quantity: 6, reason: 'Protéine polyvalente' },
    { name: 'Beurre', quantity: 1, reason: 'Produit de base' },
    { name: 'Fromage', quantity: 1, reason: 'Produit laitier courant' }
  ],
  due: [],
  seasonal: []
};

export default function SmartFeaturesBar({ userId, currentListId, onAddItems }: SmartFeaturesBarProps) {
  const [showPanel, setShowPanel] = useState(false);
  const [activeFeature, setActiveFeature] = useState<'suggestions' | 'recipes' | 'alerts' | 'share' | null>(null);
  const [loading, setLoading] = useState(false);
  
  // States
  const [suggestions, setSuggestions] = useState<SmartSuggestions | null>(null);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [alerts, setAlerts] = useState<PromoAlert[]>([]);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [recipeIngredients, setRecipeIngredients] = useState<string[]>([]);
  const [currentIngredient, setCurrentIngredient] = useState('');
  const [generatedRecipe, setGeneratedRecipe] = useState<Recipe | null>(null);
  const ingredientInputRef = useRef<HTMLInputElement>(null);

  // Load alerts on mount
  useEffect(() => {
    loadAlerts();
  }, [userId]);

  const loadAlerts = async () => {
    try {
      const data = await alertsService.getAlerts(userId, true);
      setAlerts(data);
      setUnreadAlerts(data.length);
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  // ===== SUGGESTIONS IA =====
  const handleLoadSuggestions = async () => {
    setLoading(true);
    try {
      const data = await aiContextService.getSuggestions(userId);
      
      // Vérifier si l'utilisateur a un historique
      const hasHistory = data.frequent && data.frequent.length > 0;
      
      if (!hasHistory) {
        // Premier utilisateur - montrer les suggestions par défaut
        setSuggestions(DEFAULT_SUGGESTIONS);
        setIsFirstTime(true);
      } else {
        setSuggestions(data);
        setIsFirstTime(false);
      }
      
      setActiveFeature('suggestions');
      setShowPanel(true);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      // En cas d'erreur, montrer quand même les suggestions par défaut
      setSuggestions(DEFAULT_SUGGESTIONS);
      setIsFirstTime(true);
      setActiveFeature('suggestions');
      setShowPanel(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuggestion = (item: any) => {
    if (onAddItems) {
      onAddItems([{
        name: item.name,
        quantity: item.quantity,
        category: 'Épicerie',
        checked: false
      }]);
    }
  };

  // ===== RECETTES =====
  const handleAddIngredient = () => {
    const ingredient = currentIngredient.trim();
    if (ingredient) {
      setRecipeIngredients([...recipeIngredients, ingredient]);
      setCurrentIngredient('');
      ingredientInputRef.current?.focus();
    }
  };

  const handleIngredientKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddIngredient();
    }
  };

  const handleGenerateRecipe = async () => {
    if (recipeIngredients.length === 0) {
      alert('⚠️ Ajoutez au moins un ingrédient');
      return;
    }

    setLoading(true);
    try {
      const recipe = await recipesService.generate(recipeIngredients);
      setGeneratedRecipe(recipe);
    } catch (error) {
      console.error('Error generating recipe:', error);
      alert('❌ Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const handleRecipeToList = async () => {
    if (!generatedRecipe) return;

    setLoading(true);
    try {
      const items = generatedRecipe.ingredients.map(ing => ({
        name: ing.name,
        quantity: ing.quantity,
        category: ing.category || 'Épicerie',
        unit: ing.unit,
        checked: false
      }));

      if (onAddItems && items.length > 0) {
        onAddItems(items);
        alert(`✅ ${items.length} ingrédient${items.length > 1 ? 's' : ''} ajouté${items.length > 1 ? 's' : ''} à la liste!`);
        setShowPanel(false);
        setGeneratedRecipe(null);
        setRecipeIngredients([]);
      } else {
        alert('⚠️ Aucun ingrédient à ajouter');
      }
    } catch (error) {
      console.error('Error converting recipe:', error);
      alert('❌ Erreur lors de la conversion');
    } finally {
      setLoading(false);
    }
  };

  // ===== ALERTES =====
  const handleScanAlerts = async () => {
    setLoading(true);
    try {
      const result = await alertsService.scan(userId);
      await loadAlerts();
      alert(`🎉 Trouvé ${result.matches} promotions pour vous!`);
    } catch (error) {
      console.error('Error scanning alerts:', error);
      alert('❌ Erreur lors du scan');
    } finally {
      setLoading(false);
    }
  };

  // ===== PARTAGE =====
  const handleShare = async () => {
    if (!currentListId) {
      alert('⚠️ Sélectionnez une liste à partager');
      return;
    }

    const email = prompt('📧 Email de la personne à inviter:');
    if (!email) return;

    setLoading(true);
    try {
      await collaborativeService.shareList(currentListId, email, 'edit');
      alert('✅ Liste partagée avec succès!');
    } catch (error) {
      console.error('Error sharing:', error);
      alert('❌ Erreur lors du partage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Barre de fonctionnalités */}
      <div className="smart-features-bar">
        <button 
          className="feature-btn suggestions"
          onClick={handleLoadSuggestions}
          disabled={loading}
          title="Suggestions intelligentes basées sur vos achats"
        >
          🧠 Suggestions IA
        </button>

        <button 
          className="feature-btn recipes"
          onClick={() => {
            setActiveFeature('recipes');
            setShowPanel(true);
          }}
          title="Générer une recette avec l'IA"
        >
          👨‍🍳 Recettes
        </button>

        <button 
          className="feature-btn alerts"
          onClick={() => {
            setActiveFeature('alerts');
            setShowPanel(true);
          }}
          title="Voir les promotions personnalisées"
        >
          🔔 Alertes
          {unreadAlerts > 0 && <span className="badge">{unreadAlerts}</span>}
        </button>

        <button 
          className="feature-btn share"
          onClick={handleShare}
          disabled={!currentListId || loading}
          title="Partager cette liste"
        >
          🤝 Partager
        </button>
      </div>

      {/* Panel latéral */}
      {showPanel && (
        <div className="smart-panel-overlay" onClick={() => setShowPanel(false)}>
          <div className="smart-panel" onClick={(e) => e.stopPropagation()}>
            <div className="panel-header">
              <h3>
                {activeFeature === 'suggestions' && '🧠 Suggestions Intelligentes'}
                {activeFeature === 'recipes' && '👨‍🍳 Générateur de Recettes'}
                {activeFeature === 'alerts' && '🔔 Alertes Promo'}
              </h3>
              <button className="close-btn" onClick={() => setShowPanel(false)}>✕</button>
            </div>

            <div className="panel-content">
              {/* SUGGESTIONS */}
              {activeFeature === 'suggestions' && suggestions && (
                <div className="suggestions-content">
                  {/* Message pour nouveaux utilisateurs */}
                  {isFirstTime && (
                    <div className="info-banner">
                      <div className="info-icon">💡</div>
                      <div>
                        <strong>Première utilisation ?</strong>
                        <p>Voici des suggestions de base pour démarrer. Plus vous utiliserez l'app, plus les suggestions seront personnalisées !</p>
                      </div>
                    </div>
                  )}

                  {suggestions.frequent.length > 0 ? (
                    <div className="suggestion-group">
                      <h4>📊 {isFirstTime ? 'Suggestions de départ' : 'Articles fréquents'}</h4>
                      <div className="suggestion-list">
                        {suggestions.frequent.slice(0, 5).map((item, i) => (
                          <div key={i} className="suggestion-item">
                            <div>
                              <strong>{item.name}</strong>
                              <span className="quantity">x{item.quantity}</span>
                              <p className="reason">{item.reason}</p>
                            </div>
                            <button onClick={() => handleAddSuggestion(item)}>+</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">📦</div>
                      <p><strong>Aucune suggestion pour le moment</strong></p>
                      <p>Ajoutez des articles à votre liste et cochez-les quand vous les achetez. L'IA apprendra vos habitudes !</p>
                    </div>
                  )}

                  {suggestions.due.length > 0 && (
                    <div className="suggestion-group">
                      <h4>⏰ À racheter</h4>
                      <div className="suggestion-list">
                        {suggestions.due.map((item, i) => (
                          <div key={i} className="suggestion-item">
                            <div>
                              <strong>{item.name}</strong>
                              <span className="quantity">x{item.quantity}</span>
                              <p className="reason">{item.reason}</p>
                            </div>
                            <button onClick={() => handleAddSuggestion(item)}>+</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Guide d'utilisation */}
                  <div className="usage-guide">
                    <h4>🚀 Comment ça fonctionne ?</h4>
                    <ol>
                      <li>Ajoutez des articles à votre liste</li>
                      <li>Cochez-les quand vous les achetez</li>
                      <li>L'IA analyse vos habitudes</li>
                      <li>Recevez des suggestions personnalisées !</li>
                    </ol>
                    <p className="tip">💡 Astuce : Plus vous utilisez l'app, meilleures seront les suggestions (optimal après 20-30 achats)</p>
                  </div>
                </div>
              )}

              {/* RECETTES - Identique à avant */}
              {activeFeature === 'recipes' && (
                <div className="recipes-content">
                  {!generatedRecipe ? (
                    <>
                      <h4>Ingrédients disponibles</h4>
                      <div className="input-with-button">
                        <input
                          ref={ingredientInputRef}
                          type="text"
                          value={currentIngredient}
                          onChange={(e) => setCurrentIngredient(e.target.value)}
                          onKeyPress={handleIngredientKeyPress}
                          placeholder="Ex: tomates, poulet..."
                        />
                        <button 
                          className="add-btn"
                          onClick={handleAddIngredient}
                          disabled={!currentIngredient.trim()}
                        >
                          +
                        </button>
                      </div>
                      <div className="ingredients-list">
                        {recipeIngredients.map((ing, i) => (
                          <span key={i} className="ingredient-tag">
                            {ing}
                            <button onClick={() => setRecipeIngredients(recipeIngredients.filter((_, idx) => idx !== i))}>×</button>
                          </span>
                        ))}
                      </div>
                      <button 
                        className="primary-btn"
                        onClick={handleGenerateRecipe}
                        disabled={loading || recipeIngredients.length === 0}
                      >
                        {loading ? '⏳ Génération...' : `✨ Générer une recette (${recipeIngredients.length})`}
                      </button>
                    </>
                  ) : (
                    <div className="recipe-result">
                      <h4>{generatedRecipe.name}</h4>
                      <p>{generatedRecipe.description}</p>
                      <div className="recipe-meta">
                        <span>👥 {generatedRecipe.servings} pers.</span>
                        {generatedRecipe.prepTime && <span>⏱️ {generatedRecipe.prepTime} min</span>}
                        {generatedRecipe.difficulty && <span>📊 {generatedRecipe.difficulty}</span>}
                      </div>
                      
                      {generatedRecipe.ingredients && generatedRecipe.ingredients.length > 0 && (
                        <div className="ingredients-preview">
                          <h5>📋 Ingrédients ({generatedRecipe.ingredients.length})</h5>
                          <ul>
                            {generatedRecipe.ingredients.slice(0, 5).map((ing, i) => (
                              <li key={i}>
                                {ing.quantity} {ing.unit} {ing.name}
                              </li>
                            ))}
                            {generatedRecipe.ingredients.length > 5 && (
                              <li><em>+ {generatedRecipe.ingredients.length - 5} autres...</em></li>
                            )}
                          </ul>
                        </div>
                      )}

                      <button 
                        className="primary-btn" 
                        onClick={handleRecipeToList}
                        disabled={loading}
                      >
                        {loading ? '⏳ Ajout...' : '📝 Ajouter les ingrédients à la liste'}
                      </button>
                      <button className="secondary-btn" onClick={() => {
                        setGeneratedRecipe(null);
                        setRecipeIngredients([]);
                      }}>
                        ← Nouvelle recette
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ALERTES - Identique */}
              {activeFeature === 'alerts' && (
                <div className="alerts-content">
                  <button className="primary-btn" onClick={handleScanAlerts} disabled={loading}>
                    {loading ? '⏳ Scan...' : '🔍 Scanner les promotions'}
                  </button>

                  <div className="alerts-list">
                    {alerts.length === 0 ? (
                      <p className="empty-state">Aucune alerte pour le moment.<br/>Cliquez sur "Scanner" !</p>
                    ) : (
                      alerts.map((alert) => (
                        <div key={alert.id} className="alert-item">
                          <div className="alert-icon">🏷️</div>
                          <div className="alert-info">
                            <strong>{alert.promotion?.productName}</strong>
                            <div className="alert-discount">{alert.promotion?.discount}</div>
                            {alert.promotion?.price && <span className="alert-price">{alert.promotion.price}</span>}
                            <p className="alert-match">
                              {alert.matchType === 'exact' ? '✅ Correspond à vos achats' : '📂 Catégorie favorite'}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .smart-features-bar {
          display: flex;
          gap: 10px;
          padding: 16px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin-bottom: 20px;
        }

        .feature-btn {
          flex: 1;
          padding: 12px 16px;
          border: 2px solid #e0e0e0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s;
          position: relative;
        }

        .feature-btn:hover:not(:disabled) {
          border-color: #4CAF50;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2);
        }

        .feature-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background: #f44336;
          color: white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
        }

        .info-banner {
          background: #e3f2fd;
          border-left: 4px solid #2196f3;
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: flex;
          gap: 12px;
        }

        .info-icon {
          font-size: 24px;
          flex-shrink: 0;
        }

        .info-banner strong {
          display: block;
          margin-bottom: 4px;
          color: #1976d2;
        }

        .info-banner p {
          margin: 0;
          color: #424242;
          font-size: 14px;
        }

        .usage-guide {
          background: #f5f5f5;
          padding: 16px;
          border-radius: 8px;
          margin-top: 20px;
        }

        .usage-guide h4 {
          margin: 0 0 12px 0;
          color: #333;
        }

        .usage-guide ol {
          margin: 0 0 12px 0;
          padding-left: 20px;
        }

        .usage-guide li {
          margin: 6px 0;
          color: #666;
        }

        .tip {
          background: #fff3e0;
          padding: 8px 12px;
          border-radius: 6px;
          margin: 0;
          font-size: 13px;
          color: #e65100;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #999;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-state strong {
          display: block;
          color: #333;
          margin-bottom: 8px;
        }

        .smart-panel-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 1000;
          display: flex;
          justify-content: flex-end;
        }

        .smart-panel {
          width: 100%;
          max-width: 500px;
          background: white;
          height: 100vh;
          overflow-y: auto;
          box-shadow: -4px 0 20px rgba(0,0,0,0.15);
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e0e0e0;
          position: sticky;
          top: 0;
          background: white;
          z-index: 10;
        }

        .panel-header h3 {
          margin: 0;
          font-size: 20px;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          padding: 8px;
          color: #666;
        }

        .panel-content {
          padding: 20px;
        }

        .suggestion-group {
          margin-bottom: 24px;
        }

        .suggestion-group h4 {
          margin: 0 0 12px 0;
          color: #333;
        }

        .suggestion-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #f5f5f5;
          border-radius: 8px;
          margin-bottom: 8px;
        }

        .suggestion-item strong {
          display: block;
          margin-bottom: 4px;
        }

        .quantity {
          color: #4CAF50;
          font-weight: bold;
          margin-left: 8px;
        }

        .reason {
          color: #666;
          font-size: 12px;
          margin: 4px 0 0 0;
        }

        .suggestion-item button {
          background: #4CAF50;
          color: white;
          border: none;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          font-size: 20px;
          cursor: pointer;
        }

        .input-with-button {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }

        .input-with-button input {
          flex: 1;
          padding: 12px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 14px;
          margin: 0;
        }

        .add-btn {
          width: 48px;
          height: 48px;
          background: #4CAF50;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 24px;
          font-weight: bold;
          cursor: pointer;
          flex-shrink: 0;
        }

        .add-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .primary-btn {
          width: 100%;
          padding: 14px;
          background: #4CAF50;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          margin-top: 16px;
        }

        .primary-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .secondary-btn {
          width: 100%;
          padding: 14px;
          background: #f5f5f5;
          color: #333;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          margin-top: 8px;
        }

        .ingredients-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 16px;
          min-height: 40px;
        }

        .ingredient-tag {
          background: #e8f5e9;
          color: #2e7d32;
          padding: 6px 12px;
          border-radius: 16px;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .ingredient-tag button {
          background: none;
          border: none;
          color: #2e7d32;
          font-size: 18px;
          cursor: pointer;
          padding: 0;
        }

        .recipe-result {
          background: #f5f5f5;
          padding: 20px;
          border-radius: 12px;
        }

        .recipe-meta {
          display: flex;
          gap: 16px;
          margin: 12px 0;
          color: #666;
        }

        .ingredients-preview {
          background: white;
          padding: 12px;
          border-radius: 8px;
          margin: 12px 0;
        }

        .ingredients-preview h5 {
          margin: 0 0 8px 0;
          color: #333;
        }

        .ingredients-preview ul {
          margin: 0;
          padding-left: 20px;
        }

        .ingredients-preview li {
          margin: 4px 0;
          color: #666;
        }

        .alert-item {
          display: flex;
          gap: 12px;
          padding: 16px;
          background: #fff3e0;
          border-left: 4px solid #ff9800;
          border-radius: 8px;
          margin-bottom: 12px;
        }

        .alert-icon {
          font-size: 24px;
        }

        .alert-info strong {
          display: block;
          margin-bottom: 4px;
        }

        .alert-discount {
          color: #f44336;
          font-weight: bold;
          font-size: 18px;
          margin: 4px 0;
        }

        .alert-price {
          color: #666;
          margin-left: 8px;
        }

        .alert-match {
          color: #666;
          font-size: 12px;
          margin: 4px 0 0 0;
        }

        @media (max-width: 768px) {
          .smart-features-bar {
            flex-wrap: wrap;
          }
          
          .feature-btn {
            flex: 1 1 calc(50% - 5px);
          }

          .smart-panel {
            max-width: 100%;
          }
        }
      `}</style>
    </>
  );
}
